import { useState, useCallback } from 'react'
import { apiFetch } from '@/lib/api'

export interface TriggerOptions {
  pathOverride?: string
}

export interface UseApiMutationResult<T, B> {
  trigger: (body?: B, opts?: TriggerOptions) => Promise<T | null>
  data: T | null
  error: string | null
  isLoading: boolean
  reset: () => void
}

export function useApiMutation<T = unknown, B = unknown>(
  path: string,
  method: string = 'POST',
): UseApiMutationResult<T, B> {
  const [state, setState] = useState<{ data: T | null; error: string | null; isLoading: boolean }>({
    data: null, error: null, isLoading: false,
  })

  const trigger = useCallback(async (body?: B, opts?: TriggerOptions) => {
    const target = opts?.pathOverride ?? path
    setState({ data: null, error: null, isLoading: true })
    try {
      const data = await apiFetch<T>(target, {
        method,
        body: body ? JSON.stringify(body) : undefined,
      })
      setState({ data, error: null, isLoading: false })
      return data
    } catch (e: any) {
      setState({ data: null, error: e.message, isLoading: false })
      return null
    }
  }, [path, method])

  return {
    trigger,
    ...state,
    reset: () => setState({ data: null, error: null, isLoading: false }),
  }
}
